import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import { useState } from 'react';
import { DepSelectStep } from './DepSelectStep';
import { OptSelectStep } from './OptSelectStep';
import { OvrdSelectStep } from './OvrdSelectStep';
import { PeriodSelectStep } from './PeriodSelectStep';
import { PolSelectStep } from './PolSelectStep';
import { ProdSelectStep } from './ProdSelectStep';
import { ReallocSelectStep } from './ReallocSelectStep';

/**
 * Per requirements GP-9, the wizard creates a Calc Chain Request and subsequently runs a calculation
 */
export const CalcJobDialog = ({ data, closeDialog }) => {
  const stepperSpecs = {
    Periods: {
      icon: fas.faCalendar,
      component: PeriodSelectStep,
    },
    Policy: {
      icon: fas.faBook,
      component: PolSelectStep,
    },
    Products: {
      icon: fas.faPills,
      component: ProdSelectStep,
    },
    Overrides: {
      icon: fas.faPencil,
      component: OvrdSelectStep,
    },
    Dependencies: {
      icon: fas.faPlug,
      component: DepSelectStep,
    },
    Reallocations: {
      icon: fas.faObjectUngroup,
      component: ReallocSelectStep,
    },
    Options: {
      icon: fas.faSliders,
      component: OptSelectStep,
    },
  };

  const rsrc = useRsrc();
  const { navigateTo } = useNavState();

  const [slideDir, setSlideDir] = useState(null);

  const [currStep, setCurrStep] = useState('Periods');
  const [nextStep, setNextStep] = useState(null);
  const [stepHist, setStepHist] = useState([]);
  const [stepReady, setStepReady] = useState(true);

  // This object is passed to the steps and mutated to build the calc
  const [calcChainReq, setCalcChainReq] = useState({ entity: data.entity, calcJobReqs: [], prods: [], prodSets: [] });
  const runCalc = () => {
    calcChainReq.name = calcChainReq.liabPolType.displayName + ' Calculation Chain';
    calcChainReq.liabPolType = calcChainReq.liabPolType.code;

    calcChainReq.calcJobReqs.forEach((calcJobReq) => {
      calcJobReq.envCode = calcChainReq.envCode;
      calcJobReq.prods = calcChainReq.prods;
      calcJobReq.prodSets = calcChainReq.prodSets;
      calcJobReq.bundles = calcChainReq.bundles;
      calcJobReq.reallocMode = calcChainReq.reallocMode;

      calcJobReq.polType = calcJobReq.polType.code;

      if (calcJobReq.calcDeps) {
        // Must remove RowId from CalcDeps for deserialization in the BE
        const calcDeps = [];
        calcJobReq.calcDeps.forEach((calcDep) =>
          calcDeps.push({ src: calcDep.src, srcId: calcDep.srcId, type: calcDep.type }),
        );
        calcJobReq.calcDeps = calcDeps;
      }
    });

    closeDialog();

    const reqParam = { calcChainReq: JSON.stringify(calcChainReq) };
    rsrc.store('gp', 'CALC_JOB_STORE_PLUGIN', reqParam).then((job) => {
      rsrc.start(job.id).then((run) => {
        const navObj = { entityId: job.id, mode: Mode.DETAIL };
        navigateTo('skdr/job', navObj);
      });
    });
  };

  const handleNext = () => {
    setSlideDir('left');
    const newStepHist = [...stepHist, currStep];
    setStepHist(newStepHist);
    setCurrStep(nextStep);
  };

  const handleBack = () => {
    setSlideDir('right');
    const newHist = [...stepHist];
    const prevStep = newHist.pop();
    setStepHist(newHist);
    setCurrStep(prevStep);
  };

  const StepIcon = ({ ownerState }) => {
    const stepName = ownerState.name;
    const isActive = ownerState.active;
    const isCompleted = ownerState.completed;

    const resolvedIcon = isCompleted ? fas.faCheck : stepperSpecs[stepName].icon;
    const bgColor = isCompleted || isActive ? '#1976d2' : '#999';
    return (
      <Box
        style={{
          backgroundColor: bgColor,
          borderRadius: '50%',
          width: 32,
          height: 32,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <FontAwesomeIcon icon={resolvedIcon} style={{ color: '#fff', fontSize: 16 }} />
      </Box>
    );
  };

  const StepComponent = stepperSpecs[currStep].component;
  const stepList = Object.keys(stepperSpecs);
  const isLastStep = stepList.indexOf(currStep) === stepList.length - 1;

  return (
    <Dialog
      onClose={closeDialog}
      open
      fullWidth
      maxWidth="xl"
      slotProps={{
        paper: {
          sx: {
            height: '90%',
            maxHeight: '90%',
            width: '80%',
            maxWidth: '80%',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <DialogTitle>
        <Stepper activeStep={Object.keys(stepperSpecs).indexOf(currStep)}>
          {Object.keys(stepperSpecs).map((name) => (
            <Step key={name}>
              <StepLabel slots={{ stepIcon: StepIcon }} slotProps={{ stepIcon: { ownerState: { name: name } } }}>
                {name}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Slide direction={slideDir} in={true} appear timeout={300} easing="ease-out" key={currStep}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <StepComponent
              calcChainReq={calcChainReq}
              setCalcChainReq={setCalcChainReq}
              setNextStep={setNextStep}
              setStepReady={setStepReady}
              {...(isLastStep ? { runCalc: runCalc } : {})}
            />
          </Box>
        </Slide>
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button sx={{ visibility: stepHist.length > 0 ? 'visible' : 'hidden', minWidth: 120 }} onClick={handleBack}>
            Back
          </Button>

          <Button sx={{ minWidth: 120 }} disabled={!nextStep || !stepReady} onClick={isLastStep ? runCalc : handleNext}>
            {isLastStep ? 'Run' : 'Next'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
